import type { FilterFieldType, FilterGroup, GroupLogic } from './filter';
import type { FlowPeopleScope } from './people';
import { CONTACT_FIELDS, USAGE_ROLE_LABEL } from './contact-view';
import { ACCOUNT_FIELDS } from './account-view';
import { ROLE_LABEL } from '../product-usage/roles';
import { isValidOperatorForType, validateOperatorValueShape, RELATIVE_HOUR_OPERATORS } from './filter-operators';
import { TRACKER_BUCKETS } from './tracker';
import { PILL_TYPE_FILTER_VALUES } from '../schemas/flow-contact-state';
import { SIGNAL_TYPES, ACTION_STATES, SOURCE_SYSTEMS, CAMPAIGN_STATUS_ORDER, SIGNAL_ACTION_STATUS_ORDER } from '../schemas/terra-signals';
import { normalizeGroupBy } from './view';
import { groupContiguous, unifyLabels } from './field-catalog-order';
import { withHints, CONTACT_CATALOG_HINTS, ACCOUNT_CATALOG_HINTS, SIGNAL_CATALOG_HINTS } from './field-hints';
// One-way: signal-view.ts is a pure leaf (like contact-view.ts/account-view.ts
// above) with no runtime import of this file — see signal-view.ts's own
// header comment for why that direction is load-bearing, not incidental.
import { PLAYS_FILTER_FIELDS, PLAYS_GROUP_KEYS } from './plays-view';
import { PLAYS_TABLE_COLUMNS, PLAYS_SORTABLE_KEYS } from './plays-table';
import { SIGNAL_FIELDS, SIGNAL_DISPLAY_FILTER_FIELDS, SIGNAL_SYNTHETIC_SORT_KEYS, SIGNAL_SYNTHETIC_GROUP_KEYS, SIGNAL_LEGACY_COLUMN_KEYS, SIGNAL_DROPPED_COLUMN_KEYS } from './signal-view';

/**
 * Shared filterable-field catalogs — the Filters popover's field picker
 * (U4) and the server's `FIELD_MAP` (`src/api/flow.ts`, U3B2) both consume
 * this module, so a field's key/label/group/type is defined exactly once
 * (Schemas-as-source-of-truth).
 *
 * DECOUPLED from visible columns (`CONTACT_DEFAULT_COLUMNS` /
 * `ACCOUNT_DEFAULT_COLUMNS`): a field can be filterable without being a
 * table column, and every filterable field here is a candidate column —
 * the two lists are related but not identical (R5).
 *
 * `key` is load-bearing: U3B2 builds a same-named `FilterFieldDef` in
 * `CONTACT_FIELD_MAP` / `ACCOUNT_FIELD_MAP` for every entry here, and
 * bidirectional contract tests (flow-contract.test.ts /
 * flow-account-contract.test.ts) assert the two lists match exactly.
 * Renaming a key here is a breaking change to that contract.
 *
 * Signals V3 PR 1 additions to the entry shape and composition (docs:
 * `docs/flow/schema-and-filter-readability.md` rule 11):
 *  - `granularity: 'day'` — a Postgres DATE field: no hour operators.
 *  - `groupContiguous` / `unifyLabels` (`field-catalog-order.ts`) fold the
 *    Signals catalog, which is stitched from several lists, so each picker
 *    heading prints once and a key shared with a column has one label.
 */

export interface FilterFieldCatalogEntry {
  key: string;
  label: string;
  /** Section label for the field picker ('Contact' / 'Account' / 'Tracker') — Action View's labeling pattern. */
  group: string;
  type: FilterFieldType;
  /**
   * Filterable but not safe to `sort`/`group_by` — mirrors the server's
   * `FilterFieldDef.filterOnly` (src/services/flow/query/condition-to-sql.ts)
   * 1:1, so this catalog can drive a sort/group-by field picker that never
   * offers a field the backend would 400 on. Explicit flag, set only on the
   * EXISTS-backed (`ACCOUNT_CONTACT_EXISTS_FIELDS`) and cross-join
   * (`CONTACT_ACCOUNT_CROSS_JOIN_FIELDS`) entries below — never derived.
   */
  filterOnly?: boolean;
  /**
   * Evaluated only in the browser over the server result, so it cannot participate in an OR with other
   * conditions. `andOnlyPlacementError` is the one rule; the Filters editor and saved-view writes both enforce it.
   */
  andOnly?: boolean;
  /**
   * Enumerated values for a picklist-typed field (U7 filter-side cleanup) —
   * when present, the Filters popover renders a real select/multi-select off
   * this list instead of free-typing (`FilterPopover.tsx`'s `ConditionRow`),
   * and `MultiValueInput` (U8) uses it as its suggestion list for
   * `in_any_of`. Not derived from anywhere generic — set explicitly per
   * field, same as `filterOnly`.
   *
   * Two shapes (plan 001 A1, retained under A4 for ONE field). A plain
   * `string` is its own display label — that is every pre-existing entry,
   * unchanged. A `{ value, label }` pair STORES `value` and DISPLAYS `label`.
   *
   * Its one live use is the Plays list's `play_type`
   * (the Plays `FlowTable` mount, `plays/playsTableConfig.tsx`), which stores the play-type SLUG
   * (`event_invite`) and displays the Play Type NAME (`Event Invite`). That is
   * a slug↔display-name pairing for a TYPE, not an id standing in for a
   * record, so it does not violate A4's rule (no ids as filter values): the
   * rep is picking a kind of play from a short, stable, named list.
   *
   * A1 also used this form for a `signal_instance_id` "Play" field whose
   * stored value was a campaign/flow record id. A4 deleted that field —
   * presets scope by `signal_name contains "<play name>"` instead (A16). Do
   * not reintroduce a record id here behind a label.
   *
   * Never read this field directly: `valueOptionsOf` (render) and
   * `valueStringsOf` (validate) below normalize both shapes, so a consumer
   * cannot accidentally handle only one of them.
   */
  values?: Array<string | { value: string; label: string }>;
  /**
   * QA round 8 Codex fixes, F12/F14 — `values` are SUGGESTIONS for this field,
   * not the closed set of legal values. Set on the Playbooks campaign-member
   * picklists (`mini-apps/playbooks/campaign-member-filter-fields.ts`): their
   * lists are hand-maintained mirrors of Salesforce picklists Terra does not
   * sync (the module's own hardcoded-list note), and a rep's already-saved
   * `cm_status equals Invited` — Launcher's own default status, missing from
   * the list — must never block "Apply filters". Effects: `valueStringsOf`
   * returns `undefined` (the documented "skip the membership check" spelling,
   * so `useFilterDraft`, `validateViewFieldReferences` and `flow-plays` all
   * skip it at once); `FilterPopover` renders a free-text input with the
   * values as `<datalist>` suggestions instead of a closed `<select>`. A field
   * WITHOUT this flag (the tracker's derived Status buckets) is unchanged.
   * Only meaningful with plain-string `values` — labeled `{ value, label }`
   * options store ids a rep cannot type.
   */
  allowCustomValue?: boolean;
  /**
   * Opt-in: render this picklist's `in_any_of` / `not_in_any_of` value as a list of every
   * option to tick, instead of the free-entry chips input whose options only appear as
   * suggestions. Optional and additive: omitting it (every field but the Home board's) is a
   * no-op. The stored value is the same array of option values either way, so
   * serialization and validation do not change. Needs `values`; has no effect on a
   * `allowCustomValue` field, which is open by definition.
   */
  valueInput?: 'closed-list';
  /**
   * Narrows the operators this SPECIFIC field accepts, below whatever
   * `FILTER_OPERATORS_BY_TYPE[type]` (filter-operators.ts) would otherwise
   * allow for its type. Optional/additive — omitting it (every existing
   * field) is a no-op, unchanged from before this flag existed.
   *
   * Introduced for the Terra Actions pill-type fields (`state_pill_type` /
   * `contact_state_pill_type`, custom-views v0.1): both are `filterOnly`
   * `exists`-backed picklist fields, and `not_equals`/`is_empty`/
   * `is_not_empty` evaluate INSIDE the EXISTS subquery (condition-to-sql.ts's
   * documented caveat) — "has a pill that isn't X" / "has a pill with an
   * empty type" (always false; `pill_type` is never blank), not "has no X
   * pill". That reading is misleading enough for THIS field (Spencer decision,
   * 2026-08-12) to restrict it to `equals`/`in_any_of` only, even though every
   * other `exists`-backed picklist field in this file (e.g. `contact_stage`)
   * carries the identical caveat unrestricted — this is a narrow, explicit
   * exception, not a precedent to generalize.
   *
   * Enforced at WRITE time only (`validateViewFieldReferences` below, the
   * same 400-on-save gate `FILTER_OPERATORS_BY_TYPE` mismatches already use)
   * — there is no equivalent gate for ad-hoc (unsaved) list-endpoint filters
   * today for ANY field, so this doesn't newly restrict those relative to
   * every other field's existing behavior.
   */
  allowedOperators?: readonly string[];
  /**
   * Which `/api/flow/people?scope=` variant the person picker
   * (`PersonValuePicker`/`PersonMultiValuePicker`) queries for THIS field,
   * instead of the full unscoped directory. Set ONLY on `ACCOUNT_FILTER_FIELDS`'
   * `owner_name`/`claydr_name` entries — the confirmed business invariant
   * (product, not `detectFlowRole`): an Account can only be owned by a
   * GTME/GS, and an Account's ClayDR can only ever be a ClayDR. Every other
   * person-typed field (including `ACCOUNT_FILTER_FIELDS`' own `gs_name`,
   * and all of `CONTACT_FILTER_FIELDS`' owner_name/claydr_name/gs_name)
   * deliberately keeps the full unscoped directory — there is no equivalent
   * guaranteed-membership invariant for those.
   */
  peopleScope?: FlowPeopleScope;
  /**
   * `'day'` on a `date`-typed field whose column Postgres stores as a calendar
   * DATE (no clock): the hour-granular relative operators ("within the last 24
   * hours") are meaningless there, so the operator menu omits them and
   * `validateViewFieldReferences` rejects them. Unset = the column has a clock
   * (timestamptz) and every date operator applies. Set by hand (this module is
   * browser-safe and cannot read `PG_DATE_COLUMN_TYPES`);
   * `src/api/__tests__/flow-date-granularity-contract.test.ts` pins every
   * catalog's flags against the real column types in both directions.
   * Signals V3 PR 1, U6.
   */
  granularity?: 'day';
  /**
   * Hand-written example values for a field with NO closed `values` list — the
   * muted second line under its name in every field picker, and searchable
   * ("call" finds Step type). Written in `field-hints.ts` (tables keyed by field
   * key, applied with `withHints`), not per entry. Read through `fieldExamples`
   * (`field-catalog-order.ts`), which prefers a derived `values` line over this.
   */
  hint?: string;
}

/**
 * A catalog entry's enumerated `values` normalized for RENDERING: every
 * option as a `{ value, label }` pair, with a plain-string option becoming
 * its own label. Returns `[]` for a field with no enumerated values, so a
 * caller can map over it unconditionally.
 */
export function valueOptionsOf(entry: FilterFieldCatalogEntry): Array<{ value: string; label: string }> {
  return (entry.values ?? []).map((option) => (typeof option === 'string' ? { value: option, label: option } : option));
}

/**
 * A catalog entry's enumerated `values` normalized for VALIDATION and for
 * the components that still speak plain strings: the STORED values only,
 * never the display labels. `undefined` (not `[]`) when the field has no
 * enumerated values — `validateOperatorValueShape` treats `undefined` as
 * "no membership check", and an empty array would instead reject every
 * value. Also `undefined` when `allowCustomValue` is set (F12/F14): the
 * field's `values` are suggestions, not a closed set, so there is nothing to
 * membership-check against — that skip is exactly what lets a saved value
 * outside the hand-maintained list keep validating clean.
 */
export function valueStringsOf(entry: FilterFieldCatalogEntry): string[] | undefined {
  if (!entry.values || entry.allowCustomValue) return undefined;
  return entry.values.map((option) => (typeof option === 'string' ? option : option.value));
}

/**
 * Name-column fields that resolve to a person (owner/ClayDR/GS): the
 * `{me}` token and the person picker apply to these (Decision 6). The
 * underlying columns are unchanged text name columns — only the *filter*
 * semantics gain person-awareness, so this catalog is the only place the
 * plain-text typing on `CONTACT_FIELDS`/`ACCOUNT_FIELDS` is overridden.
 */
const PERSON_FIELD_KEYS = new Set(['owner_name', 'claydr_name', 'gs_name']);

// ── Tracker (U7 / R6) ───────────────────────────────────────────────────────
// The tracker is Terra-only per-record state (`flow_record_trackers`, joined
// into both list queries), catalogued as its own picker section rather than
// under Contact/Account: it belongs to neither mirror and applies identically
// to both entities. `tracker`/`tracker_rank` are derived CASE expressions (the
// bucket label a row groups under and its 0-4 order — Decision 10); the other
// two are plain joined columns.
//
// NOTHING here is filtered by default anywhere: a tracker never suppresses a
// row, it only groups/orders one (asserted in both contract tests).
//
// Filter-side cleanup (U7, Spencer 2026-08-12): a rep filtering "status is
// Due" needs to match every due row regardless of its date — that's this
// DERIVED bucket field, not the raw `tracker_status` enum a rep would have to
// know maps to it. So:
//  - `tracker` is relabeled "Status" and gets `values` from the in-repo
//    `TRACKER_BUCKETS` enum (`./tracker.ts` — a real code enum, the sanctioned
//    exception to no-hardcoded-fallbacks) — FilterPopover renders a real
//    select/multi-select off it instead of free-typing a bucket label.
//  - raw `tracker_status` (starred/snoozed/reminder) is REMOVED from this
//    catalog entirely — it confused QA (a rep typed the chip text
//    "Due 2026-08-11" as a value) and the bucket covers every rep-facing
//    case. It stays mapped in CONTACT_FIELD_MAP/ACCOUNT_FIELD_MAP
//    (src/api/flow.ts) so an existing saved view that already filters on it
//    keeps working — this is a picker-only removal, not a backend one (same
//    graceful-degradation shape as removing `tracker`/`tracker_status` from
//    the visible-COLUMN catalogs below).
export const TRACKER_FILTER_FIELDS: FilterFieldCatalogEntry[] = [
  { key: 'tracker',        label: 'Status',         group: 'Tracker', type: 'picklist', values: [...TRACKER_BUCKETS] },
  { key: 'tracker_date',   label: 'Tracker Date',   group: 'Tracker', type: 'date', granularity: 'day' },
  { key: 'tracker_note',   label: 'Tracker Note',   group: 'Tracker', type: 'text' },
  { key: 'tracker_rank',   label: 'Tracker Order',  group: 'Tracker', type: 'number' },
];


/**
 * Tracker keys also present in the per-entity COLUMN catalogs (they are
 * showable columns). `fromColumnFields` skips them so each lands in the picker
 * exactly once — under 'Tracker', not under 'Contact'/'Account'.
 */
const TRACKER_CATALOG_KEYS = new Set(TRACKER_FILTER_FIELDS.map(f => f.key));

/** Stamps `granularity: 'day'` on the named date fields (Postgres DATE columns) of a built catalog. See `FilterFieldCatalogEntry.granularity`. */
function markDayGranular(entries: FilterFieldCatalogEntry[], dayKeys: readonly string[]): FilterFieldCatalogEntry[] {
  const day = new Set(dayKeys);
  return entries.map(f => (day.has(f.key) ? { ...f, granularity: 'day' as const } : f));
}

function catalogType(key: string, type: FilterFieldType): FilterFieldType {
  return PERSON_FIELD_KEYS.has(key) ? 'person' : type;
}

interface SourceFieldDef {
  key: string;
  label: string;
  type: FilterFieldType;
  noFilter?: boolean;
  values?: readonly string[];
}

/**
 * Column-derived catalog entries: every non-`noFilter` field, person-typed
 * where applicable, carrying a closed picklist's `values` when it has them.
 *
 * `skipKeys` is for fields that ARE showable columns but are catalogued for
 * the picker somewhere else, under a different group and label — the tracker
 * keys always, plus (per entity) the cross-entity keys the caller passes in.
 * Without the skip the picker lists such a field twice.
 */
function fromColumnFields(
  fields: readonly SourceFieldDef[],
  group: string,
  skipKeys?: ReadonlySet<string>,
): FilterFieldCatalogEntry[] {
  return fields
    .filter(f => !f.noFilter && !TRACKER_CATALOG_KEYS.has(f.key) && !skipKeys?.has(f.key))
    .map(f => ({
      key: f.key,
      label: f.label,
      group,
      type: catalogType(f.key, f.type),
      ...(f.values ? { values: [...f.values] } : {}),
    }));
}

// ── Contact → Account cross-join fields ─────────────────────────────────────
// Allowlist per the plan's deferred-to-implementation guidance (OQ1): start
// from ACCOUNT_FIELD_MAP minus account-only internals, prefix `account_`,
// ~12-20 "obviously useful" fields. Picks recorded in the U3B1 notes file
// for Spencer's pre-merge review. `account_name` (already a base contact
// field via CONTACT_FIELDS) stays as-is and is NOT duplicated here.
//
// U5: all 12 are now real, showable, SORTABLE columns too — SELECT-aliased
// into ContactRow by the contacts endpoint — so `filterOnly` is gone from
// both this catalog and CONTACT_FIELD_MAP (the pair is contract-tested in
// both directions). They therefore ALSO appear in CONTACT_FIELDS, under
// account-prefixed COLUMN labels ("Account ARR"); the short labels below are
// the FILTER picker's, where the 'Account' group already supplies the
// prefix. `CROSS_JOIN_CATALOG_KEYS` below is what keeps the picker from
// listing each of them twice.
export const CONTACT_ACCOUNT_CROSS_JOIN_FIELDS: FilterFieldCatalogEntry[] = [
  { key: 'account_open_pipeline',             label: 'Open Pipeline',     group: 'Account', type: 'number' },
  { key: 'account_last_opportunity_stage',    label: 'Pipeline Stage',    group: 'Account', type: 'picklist' },
  { key: 'account_number_of_open_opp',        label: '# Open Opps',       group: 'Account', type: 'number' },
  { key: 'account_industry',                  label: 'Industry',          group: 'Account', type: 'text' },
  { key: 'account_company_segment',           label: 'Segment',           group: 'Account', type: 'picklist' },
  { key: 'account_sales_tier',                label: 'Sales Tier',        group: 'Account', type: 'picklist' },
  { key: 'account_arr',                       label: 'ARR',               group: 'Account', type: 'number' },
  { key: 'account_propensity_score',          label: 'Propensity',        group: 'Account', type: 'number' },
  { key: 'account_is_agency',                 label: 'Is Agency',         group: 'Account', type: 'boolean' },
  { key: 'account_engagement_status_90_days', label: 'Engagement Status', group: 'Account', type: 'picklist' },
  { key: 'account_number_of_employees',       label: 'Employees',         group: 'Account', type: 'number' },
  { key: 'account_billing_country',           label: 'Country',           group: 'Account', type: 'text' },
  { key: 'account_billing_city',              label: 'City',              group: 'Account', type: 'text' },
  // Bucket A/E follow-up (U5, 2026-08-27): populates on the first hourly
  // object-sync after deploy (Region__c ships from Middleman as of
  // terra-middleware PR #257). Plain text, not a picklist (Spencer decision,
  // 2026-08-27). NOT filterOnly, same as its 12 siblings above (the F4 fix):
  // also a real column in CONTACT_FIELDS/ContactRowSchema so sort/group_by
  // work without hitting the keyset-cursor bug F4 fixed.
  { key: 'account_region', label: 'Region', group: 'Account', type: 'text' },
  // US Fortune 500 rank of the contact's account (an integer, 1 is the largest).
  { key: 'account_f500_rank', label: 'F500 Rank', group: 'Account', type: 'number' },
  // account-level owner/ClayDR (2026-10-16); `owner_name`/`claydr_name` stay the CONTACT's own assignment for saved-view stability
  { key: 'account_owner_name', label: 'Owner', group: 'Account', type: 'person', peopleScope: 'account_owners' },
  { key: 'account_claydr_name', label: 'ClayDR', group: 'Account', type: 'person', peopleScope: 'account_claydrs' },
];

/**
 * Cross-join keys also present in CONTACT_FIELDS (they are showable columns
 * since U5). `fromColumnFields` skips them so each lands in the contact
 * picker exactly once — under 'Account' with the short label, not under
 * 'Contact' with the prefixed column label. Same role TRACKER_CATALOG_KEYS
 * plays for the tracker fields.
 */
const CROSS_JOIN_CATALOG_KEYS = new Set(CONTACT_ACCOUNT_CROSS_JOIN_FIELDS.map(f => f.key));

// ── Terra Actions pill-type filter (custom-views v0.1, Spencer 2026-08-12;
// renamed from "Pill Type" / "Terra Notes" to "Terra Actions" 2026-08-13,
// Spencer — user-visible text only, the internal `state_pill_type` key and
// PILL_TYPE_FILTER_VALUES/pill_type identifiers are unchanged) ─────────────
// Bridges the contact-state read-model's pills (normally computed at read
// time by resolveContactStateList/Detail, contact-state.ts — NOT a plain SQL
// row predicate) into the ordinary filter machinery via the read-only
// `flow_contact_state_pill_v` view + the `exists` mechanism
// (condition-to-sql.ts), same shape as `contactExists()` in src/api/flow.ts.
// `values` is PILL_TYPE_FILTER_VALUES (flow-contact-state.ts) — the single
// TS-side source of truth the view's `DISTINCT pill_type` output must stay a
// subset of (asserted by the drift contract test). `allowedOperators`
// restricts to equals/in_any_of only — see the interface doc comment above.
export const STATE_PILL_FILTER_FIELDS: FilterFieldCatalogEntry[] = [
  {
    key: 'state_pill_type',
    label: 'Terra Actions',
    group: 'Terra Actions',
    type: 'picklist',
    filterOnly: true,
    values: [...PILL_TYPE_FILTER_VALUES],
    allowedOperators: ['equals', 'in_any_of'],
  },
];

// ── Warm Intro availability (custom-views v0.2 bug fix, Spencer 2026-08-13)
// ─────────────────────────────────────────────────────────────────────────
// "This contact has a warm-intro connector AND it's actually playable right
// now" — DELIBERATELY DISTINCT from `state_pill_type = 'Warm Intro'` above.
// That field is a bare existence check over `flow_contact_state_pill_v`,
// which (Spencer decision, 2026-08-12, see the view's migration) drops ALL
// viewer-aware suppression on purpose, so it also matches a contact whose
// warm intro is already queued or was sent in the last 30 days — i.e. NOT
// actually available as a possible play. The "Warm Intros" preset
// (`contact-view.ts`) needs the suppression-AWARE reading, so this is a
// separate field with its own `exists` join
// (`WARM_INTRO_AVAILABLE_JOIN_SQL`, src/api/flow.ts) mirroring
// `warmIntroSource.resolve()`'s real predicate
// (contact-state-sources.ts) — do NOT fold this into `state_pill_type` or
// change that field's view; the two answer genuinely different questions.
// `allowedOperators: ['equals']` only: the underlying `exists` mechanism
// evaluates the operator INSIDE the subquery (condition-to-sql.ts's
// documented caveat), so "= false" would read as "has an unavailable warm
// intro" rather than the intended "has NO available warm intro" — this
// field is only ever meant to be used as "= true".
export const WARM_INTRO_AVAILABLE_FILTER_FIELDS: FilterFieldCatalogEntry[] = [
  {
    key: 'warm_intro_available',
    label: 'Warm Intro Available',
    group: 'Terra Actions',
    type: 'boolean',
    filterOnly: true,
    allowedOperators: ['equals'],
  },
];

// ── Recent Queue Send (plan 2026-09-09-003 D5, R3) ────────────────────────
// "This contact already got a queue send in the last 30 days" — the field
// behind the Plays mount's default `has_recent_queue_send = false` chip
// (`CHIP_NO_RECENT_QUEUE_SEND`, contact-view.ts), shown as a visible,
// clearable filter row rather than hidden scope.
//
// Unlike `warm_intro_available` above, this field's FIELD_MAP twin
// (`HAS_RECENT_QUEUE_SEND_SQL`, src/api/flow.ts) is a SCALAR `EXISTS`
// expression with no `exists` key — precisely so that `equals false` is
// usable. An `exists`-backed field applies the operator INSIDE its subquery
// (condition-to-sql.ts's documented caveat), so `= false` there would mean
// "has a submitted send whose flag is false" — never true — and this chip
// would silently return the whole book instead of excluding recent sends.
// `allowedOperators: ['equals']` because false/true is the entire question;
// `filterOnly` because the expression is never SELECT-aliased and so can
// never back a keyset cursor.
//
// The name intentionally matches `has_recent_queue_send` on queue-item wire
// rows (src/shared/flow/enrollment.ts) — same question, same answer.
export const RECENT_QUEUE_SEND_FILTER_FIELDS: FilterFieldCatalogEntry[] = [
  {
    key: 'has_recent_queue_send',
    label: 'Recent Queue Send (30d)',
    group: 'Terra Actions',
    type: 'boolean',
    filterOnly: true,
    allowedOperators: ['equals'],
  },
  // The DATE behind the boolean above (plan 2026-09-10-001 D4). Both read the
  // same `qs` aggregate join (`MAX(enrollment_items.updated_at)` over
  // submitted sends) in CONTACT_FIELD_MAP, so "was there a send in the last
  // 30 days" and "when was the last send" can never disagree — the drift rule
  // 7 forbids. Unlike its boolean twin this one IS a real column (SELECT-
  // aliased, sortable, groupable, in the Columns picker), so no `filterOnly`.
  // Grouped 'Terra Actions' next to the boolean rather than under 'Contact',
  // which is why CONTACT_FIELDS' entry is skipped by `fromColumnFields`.
  { key: 'last_queue_send_date', label: 'Last Queue Send Date', group: 'Terra Actions', type: 'date' },
];

// ── Clay Role (Product Usage), 2026-09-25 ───────────────────────────────────
// The filter side of the "Clay Role" column: one boolean per role, so a rep
// can build "Champion is true" or "Key builder is false"; "Any builder" is the
// fourth, a single-EXISTS twin of key builder OR builder. Labels are the chip
// labels (ROLE_LABEL) and the group is the column's own label, so the picker
// reads "Clay Role: Champion".
//
// Their FIELD_MAP twins (IS_*_SQL, src/api/flow/contact-field-map.ts) are
// SCALAR correlated EXISTS booleans, deliberately not `exists`-backed, which
// is what makes `equals false` mean "is not a champion" (the same reasoning
// as `has_recent_queue_send` above). `allowedOperators: ['equals']` because
// `is_empty`/`is_not_empty` on an EXISTS can only ever match nothing or
// everything. `filterOnly` because sorting by a correlated EXISTS is a probe
// per row across the whole mirror with no index to walk.
export const ANY_BUILDER_LABEL = 'Any builder';
export const USAGE_ROLE_FILTER_FIELDS: FilterFieldCatalogEntry[] = [
  { key: 'is_champion',    label: ROLE_LABEL.champion,    group: USAGE_ROLE_LABEL, type: 'boolean', filterOnly: true, allowedOperators: ['equals'] },
  { key: 'is_key_builder', label: ROLE_LABEL.key_builder, group: USAGE_ROLE_LABEL, type: 'boolean', filterOnly: true, allowedOperators: ['equals'] },
  { key: 'is_builder',     label: ROLE_LABEL.builder,     group: USAGE_ROLE_LABEL, type: 'boolean', filterOnly: true, allowedOperators: ['equals'] },
  // Not a role: key builder OR builder as one EXISTS (a fast single-semi-join; an OR of two EXISTS is ~8x slower on the first sorted page).
  { key: 'is_any_builder', label: ANY_BUILDER_LABEL, group: USAGE_ROLE_LABEL, type: 'boolean', filterOnly: true, allowedOperators: ['equals'] },
];

// ── Signal fields on the CONTACT catalog (plan 2026-09-10-001 D3/D13 + A1) ──
// The `terra_signals` view's rep-facing fields, filterable from the People
// surface. Their FIELD_MAP twins carry `exists: { scope: 'terra_signals' }`,
// so conditions on several of them inside ONE AND group merge into a single
// same-row `EXISTS` — "Suggested ON the Sculpt play" asks for one signal row
// satisfying both predicates, which is what makes the three Plays presets
// (Suggested / Invited / Responded) rebuildable by hand from this catalog and
// identical to the row the Plays list's menu emits (rules 2 and 9).
//
// Grouped 'Signal' rather than 'Contact': same split the tracker and the
// account cross-join fields use, so the picker lists each field once, under
// the vocabulary the Notion schema page uses. Labels and `values` match the
// Signal catalog (`TERRA_SIGNALS_FILTER_FIELDS` above) field for field — the
// two catalogs describe the same columns from two entities, and a rep who
// learns one should not have to relearn the other.
//
// `signal_subtype` is the play scope's own field under A4 — a picklist whose
// options are the live play NAMES, injected client-side, never a static enum.
// It and `signal_name` stay open-ended (campaign names, Terra-
// minted names): a free-text picklist / text field, no enumerated `values`.
export const CONTACT_SIGNAL_FILTER_FIELDS: FilterFieldCatalogEntry[] = [
  { key: 'signal_type',          label: 'Signal Type',          group: 'Signal', type: 'picklist', values: [...SIGNAL_TYPES] },
  { key: 'signal_subtype',       label: 'Signal Subtype',       group: 'Signal', type: 'picklist' },
  { key: 'signal_status',        label: 'Signal Status',        group: 'Signal', type: 'picklist', values: [...CAMPAIGN_STATUS_ORDER] },
  { key: 'has_responded',        label: 'Has Responded',        group: 'Signal', type: 'boolean' },
  { key: 'signal_name',          label: 'Signal Name',          group: 'Signal', type: 'text' },
  { key: 'signal_status_rank',   label: 'Signal Status Order',  group: 'Signal', type: 'number' },
  { key: 'signal_action_state',  label: 'Signal Action State',  group: 'Signal', type: 'picklist', values: [...ACTION_STATES] },
  { key: 'signal_source_system', label: 'Signal Source System', group: 'Signal', type: 'picklist', values: [...SOURCE_SYSTEMS] },
  // A9 — the Signals catalog's `invitee_type`, under the contact catalog's
  // `signal_`-prefixed spelling (same split as `signal_action_state` /
  // `signal_source_system`). Free text for the same reason: the launcher
  // column is uncontrolled.
  { key: 'signal_invitee_type',  label: 'Invitee Type',         group: 'Signal', type: 'text' },
  // "Actioned By" (2026-09-22 plan) — who personally completed the action
  // (invited, sent) on this signal, as opposed to `owner_name`/account
  // ownership. `{me}`-resolvable (person type), backed by
  // `CONTACT_FIELD_MAP.signal_completed_by`'s `idSql: 'ts.action_completed_by_id'`.
  { key: 'signal_completed_by',  label: 'Actioned By',          group: 'Signal', type: 'person' },
  // The Plays list's default scope, expressed on the contact catalog: "this
  // contact has a signal row on a campaign Playhouse shows". `filterOnly`
  // because its FIELD_MAP `sql` is a scalar subquery correlated to
  // `ts.signal_instance_id` — valid only inside the terra_signals EXISTS,
  // out of scope in an outer ORDER BY. `allowedOperators: ['equals']` for the
  // same reason `warm_intro_available` restricts itself: the operator
  // evaluates INSIDE the subquery, so `= false` would read "has a
  // non-visible campaign", not "has no visible campaign".
  {
    key: 'campaign_playhouse_visible',
    label: 'Playhouse Visible',
    group: 'Signal',
    type: 'boolean',
    filterOnly: true,
    allowedOperators: ['equals'],
  },
  // A4 note (deliberate absence, do not re-add): A1 added a `signal_instance_id`
  // entry here, labeled "Play", carrying the campaign/Gong-flow record id with
  // labeled options (id stored, name displayed). A4 reversed it — no id is ever
  // a rep-facing filter value. The play scope now rides `signal_subtype` above
  // (declared just below `signal_type`), whose absent `values` plays the same
  // load-bearing role A1's did: `validateViewFieldReferences` membership-checks
  // only a field that declares `values`, so a saved view scoped to any real
  // play NAME is accepted rather than rejected against a frozen list. Its
  // option list is the live plays list, injected client-side
  // (`useSignalSubtypeOptions`).
];

/**
 * Signal / queue keys catalogued ABOVE rather than under 'Contact'. They are
 * showable columns too (all but the two `filterOnly` ones are in
 * `CONTACT_FIELDS`), so `fromColumnFields` must skip them or the picker lists
 * each twice — the same role `TRACKER_CATALOG_KEYS` and
 * `CROSS_JOIN_CATALOG_KEYS` play for their groups.
 */
const SIGNAL_CATALOG_KEYS = new Set(
  [...CONTACT_SIGNAL_FILTER_FIELDS, ...RECENT_QUEUE_SEND_FILTER_FIELDS].map(f => f.key),
);

export const CONTACT_FILTER_FIELDS: FilterFieldCatalogEntry[] = withHints(markDayGranular([
  ...fromColumnFields(CONTACT_FIELDS, 'Contact', new Set([...CROSS_JOIN_CATALOG_KEYS, ...SIGNAL_CATALOG_KEYS])),
  ...CONTACT_ACCOUNT_CROSS_JOIN_FIELDS,
  ...STATE_PILL_FILTER_FIELDS,
  ...WARM_INTRO_AVAILABLE_FILTER_FIELDS,
  ...RECENT_QUEUE_SEND_FILTER_FIELDS,
  ...USAGE_ROLE_FILTER_FIELDS,
  ...CONTACT_SIGNAL_FILTER_FIELDS,
  ...TRACKER_FILTER_FIELDS,
// Postgres DATE columns on `salesforce_contact` (everything else date-like here is a timestamptz).
], ['last_activity', 'job_start_date', 'last_trial_start']), CONTACT_CATALOG_HINTS);

// ── Account → Contact EXISTS allowlist (Decision 7; expanded U6) ───────────
// Per Decision 7 (EXISTS wraps the operator inside a correlated subquery).
// Keys prefixed `contact_` to mirror the `account_` convention above and to
// avoid colliding with ACCOUNT_FIELDS keys. `src/api/flow.ts` wires these as
// `exists` entries in ACCOUNT_FIELD_MAP with `joinSql: 'SELECT 1 FROM
// salesforce_contact ec WHERE ec.account_id = sa.salesforce_account_id'` and
// `sql` values like `ec.title` (the `ec` alias matching that joinSql).
//
// U6 expanded the original 6-field starter set to every genuine per-contact
// CONTACT_FIELD_MAP field (reconstructed from the field map — see the plan's
// U6 section and the implementation report for the exact list). See the
// exclusion rationale (owner/claydr/gs name, account cross-join fields,
// tracker fields) in the ACCOUNT_FIELD_MAP comment in src/api/flow.ts.
// `contact_no_longer_at_company` is included on purpose — closes the alumni
// leak VISIBLY (no hidden filter).
export const ACCOUNT_CONTACT_EXISTS_FIELDS: FilterFieldCatalogEntry[] = [
  { key: 'contact_title',                     label: 'Title',                 group: 'Contact', type: 'text',     filterOnly: true },
  { key: 'contact_persona',                   label: 'Persona',               group: 'Contact', type: 'text',     filterOnly: true },
  { key: 'contact_stage',                     label: 'Contact Stage',         group: 'Contact', type: 'picklist', filterOnly: true },
  { key: 'contact_job_level',                 label: 'Job Level',             group: 'Contact', type: 'picklist', filterOnly: true },
  { key: 'contact_total_engagement_score',    label: 'Total Engagement',      group: 'Contact', type: 'number',   filterOnly: true },
  { key: 'contact_has_workspace',             label: 'Has Workspace',         group: 'Contact', type: 'boolean',  filterOnly: true },
  { key: 'contact_name',                      label: 'Name',                  group: 'Contact', type: 'text',     filterOnly: true },
  { key: 'contact_email',                     label: 'Email',                 group: 'Contact', type: 'text',     filterOnly: true },
  { key: 'contact_fit_score',                 label: 'Fit Score',             group: 'Contact', type: 'number',   filterOnly: true },
  { key: 'contact_icp_fit_score',              label: 'ICP Fit Score',         group: 'Contact', type: 'number',   filterOnly: true },
  { key: 'contact_persona_tier',               label: 'Persona Tier',          group: 'Contact', type: 'picklist', filterOnly: true },
  { key: 'contact_product_engagement_score',   label: 'Product Engagement',    group: 'Contact', type: 'number',   filterOnly: true },
  { key: 'contact_signals_engagement_score',   label: 'Signal Engagement',     group: 'Contact', type: 'number',   filterOnly: true },
  { key: 'contact_last_workspace_login',       label: 'Last WS Login',         group: 'Contact', type: 'date',     filterOnly: true },
  { key: 'contact_last_email_sent',            label: 'Last Email Sent',       group: 'Contact', type: 'date',     filterOnly: true },
  { key: 'contact_last_activity',              label: 'Last Activity',         group: 'Contact', type: 'date',     filterOnly: true },
  { key: 'contact_gong_in_flow',                label: 'In Flow',               group: 'Contact', type: 'boolean',  filterOnly: true },
  { key: 'contact_gong_flow_name',              label: 'Flow Name',             group: 'Contact', type: 'text',     filterOnly: true },
  { key: 'contact_lim_date',                    label: 'LIM Date',              group: 'Contact', type: 'date',     filterOnly: true },
  { key: 'contact_lim_type',                    label: 'LIM Type',              group: 'Contact', type: 'picklist', filterOnly: true },
  { key: 'contact_lim_name',                    label: 'LIM Name',              group: 'Contact', type: 'text',     filterOnly: true },
  { key: 'contact_lim_subtype',                 label: 'LIM Subtype',           group: 'Contact', type: 'picklist', filterOnly: true },
  { key: 'contact_lim_description',             label: 'LIM Description',       group: 'Contact', type: 'text',     filterOnly: true },
  { key: 'contact_lim_combined',                label: 'Last Interesting Moment', group: 'Contact', type: 'text',   filterOnly: true },
  { key: 'contact_job_start_date',              label: 'Job Start Date',        group: 'Contact', type: 'date',     filterOnly: true },
  { key: 'contact_best_phone',                  label: 'Best Phone',            group: 'Contact', type: 'text',     filterOnly: true },
  { key: 'contact_mailing_state',               label: 'Mailing State',         group: 'Contact', type: 'text',     filterOnly: true },
  { key: 'contact_mailing_country',             label: 'Mailing Country',       group: 'Contact', type: 'text',     filterOnly: true },
  { key: 'contact_last_gtme_email',             label: 'Last GTME Email',       group: 'Contact', type: 'date',     filterOnly: true },
  { key: 'contact_last_gs_meeting',             label: 'Last GS Meeting',       group: 'Contact', type: 'date',     filterOnly: true },
  { key: 'contact_last_claydr_meeting',         label: 'Last CDR Meeting',      group: 'Contact', type: 'date',     filterOnly: true },
  { key: 'contact_last_email_date',             label: 'Last Email (any)',      group: 'Contact', type: 'date',     filterOnly: true },
  { key: 'contact_last_inbound_form',           label: 'Last Inbound Form',     group: 'Contact', type: 'date',     filterOnly: true },
  { key: 'contact_no_longer_at_company',        label: 'No Longer at Co.',      group: 'Contact', type: 'boolean',  filterOnly: true },
  { key: 'contact_has_opted_out_of_email',      label: 'Opted Out',             group: 'Contact', type: 'boolean',  filterOnly: true },
  { key: 'contact_do_not_call',                 label: 'Do Not Call',           group: 'Contact', type: 'boolean',  filterOnly: true },
  { key: 'contact_suppression_reasons',         label: 'Suppression Reasons',   group: 'Contact', type: 'text',     filterOnly: true },
  { key: 'contact_gong_active_flows',           label: '# Active Flows',        group: 'Contact', type: 'number',   filterOnly: true },
  { key: 'contact_last_claydr_email',           label: 'Last CDR Email',        group: 'Contact', type: 'date',     filterOnly: true },
  { key: 'contact_last_gtme_meeting',           label: 'Last GTME Meeting',     group: 'Contact', type: 'date',     filterOnly: true },
];

// ── Terra Actions pill-type filter, account side (custom-views v0.1;
// renamed to "Terra Actions" 2026-08-13, Spencer) ──────────────────────────
// `contact_state_pill_type` is a DISTINCT key from `state_pill_type` (v2's
// recommendation) — NOT folded into ACCOUNT_CONTACT_EXISTS_FIELDS above,
// because that array's every entry shares `contactExists()`'s ONE join
// (`CONTACT_EXISTS_JOIN_SQL`, a bare `salesforce_contact ec` correlation);
// this field needs a DIFFERENT join (through `flow_contact_state_pill_v`), so
// giving it the `contact_` prefix convention but keeping it in a separate
// array from that allowlist preserves the "one key = one joinSql shape"
// invariant the bidirectional contract tests (flow-account-contract.test.ts)
// don't otherwise enforce.
// `group: 'Contact'` (the existing convention every other `contact_*`
// account-side field already uses) + `label: 'Terra Actions'` renders as
// "Contact • Terra Actions" in the field picker — Spencer's explicit ask:
// the account-side filter must read as the CONTACT column header ("Terra
// Actions", ContactFlowTable.tsx's CROSS_APP_STATE_LABEL) prefixed by its
// group, not a separately-worded label.
// Same `values`/`allowedOperators` as the contact-side field.
export const ACCOUNT_CONTACT_PILL_FILTER_FIELDS: FilterFieldCatalogEntry[] = [
  {
    key: 'contact_state_pill_type',
    label: 'Terra Actions',
    group: 'Contact',
    type: 'picklist',
    filterOnly: true,
    values: [...PILL_TYPE_FILTER_VALUES],
    allowedOperators: ['equals', 'in_any_of'],
  },
];

// ── Clay Role, account side ────────────────────────────────────────────────
// "This account has at least one live contact with that role", one boolean per
// role plus the any-builder twin. Keys follow the `contact_` + contact key
// convention of the account's other contact EXISTS fields. The group is the
// Clay Role group People uses, and the labels read as account facts ("Has Champion").
// The FIELD_MAP twins are bare EXISTS booleans like the People ones, so
// `equals false` means "has no contact with that role". Same operators and
// `filterOnly` as the People entries, for the same reasons.
export const ACCOUNT_USAGE_ROLE_FILTER_FIELDS: FilterFieldCatalogEntry[] = [
  { key: 'contact_is_champion',    label: `Has ${ROLE_LABEL.champion}`,    group: USAGE_ROLE_LABEL, type: 'boolean', filterOnly: true, allowedOperators: ['equals'] },
  { key: 'contact_is_key_builder', label: `Has ${ROLE_LABEL.key_builder}`, group: USAGE_ROLE_LABEL, type: 'boolean', filterOnly: true, allowedOperators: ['equals'] },
  { key: 'contact_is_builder',     label: `Has ${ROLE_LABEL.builder}`,     group: USAGE_ROLE_LABEL, type: 'boolean', filterOnly: true, allowedOperators: ['equals'] },
  { key: 'contact_is_any_builder', label: `Has ${ANY_BUILDER_LABEL}`, group: USAGE_ROLE_LABEL, type: 'boolean', filterOnly: true, allowedOperators: ['equals'] },
];

// U9 — Owner/ClayDR filter picker scoping. Attached via `.map()` after the
// base array literal (rather than threading a per-field option through
// `fromColumnFields`, which every OTHER caller — CONTACT_FILTER_FIELDS,
// TRACKER_FILTER_FIELDS — must NOT pick up) so exactly these two keys carry
// `peopleScope`; `gs_name` and everything else pass through untouched.
/**
 * Account hierarchy pointers as filter-only fields: no new columns or SELECT changes.
 * Only `is_empty` / `is_not_empty` are meaningful (top-level check). The Hierarchy tab PR
 * promotes them to real columns.
 */
export const ACCOUNT_HIERARCHY_FILTER_FIELDS: FilterFieldCatalogEntry[] = [
  { key: 'parent_account_id', label: 'Parent Account', group: 'Account', type: 'text', filterOnly: true, allowedOperators: ['is_empty', 'is_not_empty'] },
  { key: 'ultimate_global_parent_id', label: 'Ultimate Parent', group: 'Account', type: 'text', filterOnly: true, allowedOperators: ['is_empty', 'is_not_empty'] },
];

export const ACCOUNT_FILTER_FIELDS: FilterFieldCatalogEntry[] = withHints(markDayGranular([
  ...fromColumnFields(ACCOUNT_FIELDS, 'Account'),
  ...ACCOUNT_HIERARCHY_FILTER_FIELDS,
  ...ACCOUNT_CONTACT_EXISTS_FIELDS,
  ...ACCOUNT_CONTACT_PILL_FILTER_FIELDS,
  ...ACCOUNT_USAGE_ROLE_FILTER_FIELDS,
  ...TRACKER_FILTER_FIELDS,
].map(f => {
  if (f.key === 'owner_name') return { ...f, peopleScope: 'account_owners' as const };
  if (f.key === 'claydr_name') return { ...f, peopleScope: 'account_claydrs' as const };
  return f;
// Postgres DATE columns: the account's own renewal/GS-assign/contract-end days, and the contact-EXISTS twins of the two contact DATEs.
}), ['renewal_date', 'next_gs_assign_date', 'contract_end', 'contact_last_activity', 'contact_job_start_date']), ACCOUNT_CATALOG_HINTS);

// ── Terra Signals (Phase 1, plan 2026-09-09-002 U6) ─────────────────────────
/**
 * Wire-side filterable-field catalog for the `terra_signals` view — the
 * twin of `TERRA_SIGNALS_FIELD_MAP`
 * (`src/services/terra-signals/field-map.ts`), which owns the SQL column
 * expressions. Same relationship (and same bidirectional contract test
 * shape) as `CONTACT_FILTER_FIELDS` ↔ `CONTACT_FIELD_MAP`; the test for this
 * pair lives in `src/services/terra-signals/__tests__/field-map.test.ts`
 * rather than `flow-contract.test.ts`, which is contact-specific.
 *
 * PHASE 3 WIDENS THIS — Signals tab PR 1 (plan 2026-09-24-002 U2) wires this
 * constant into the entity switch below via the `'signal'` arm:
 * `FlowViewEntity` is now `'contact' | 'account' | 'signal'`, and
 * `filterCatalogByKey` / `isKnownFilterField` / `validateViewFieldReferences`
 * see these fields through `SIGNAL_FILTER_FIELDS` (this catalog spread with
 * `SIGNAL_DISPLAY_FILTER_FIELDS`, defined right after it below). Publishing
 * this catalog back in Phase 1 meant this widening needs no second catalog.
 *
 * `values` is populated from the shared enums for the picklists that have a
 * closed value set — including `signal_status`, whose vocabulary is
 * `CAMPAIGN_STATUS_ORDER` (the 13 Campaign statuses, led by the two
 * Terra-minted ones, `Suggested` and `HiL/Awaiting`). `signal_subtype`,
 * `signal_tactic` and `signal_instance_id` stay open-ended (campaign names,
 * ids), so they remain free-text picklists.
 *
 * `assignee` is `type: 'person'` (D3): the view carries the assignee's SFDC
 * user id (`assignee_id`), so `{me}` resolves through it exactly like
 * `owner_name`/`claydr_name`/`gs_name` do on the Contacts catalog (see the
 * field map's doc comment for the full precedent). `assignee_role` /
 * `assignee_manager` / `assignee_id` / `assignee_email` stay plain `text` —
 * only `assignee` (the name) is the `{me}`-resolvable field.
 */
export const TERRA_SIGNALS_FILTER_FIELDS: FilterFieldCatalogEntry[] = [
  // Signal identity / classification
  { key: 'signal_id',           label: 'Signal ID',           group: 'Signal', type: 'text' },
  { key: 'signal_type',         label: 'Signal category',         group: 'Signal', type: 'picklist', values: [...SIGNAL_TYPES] },
  { key: 'signal_subtype',      label: 'Signal subcategory',      group: 'Signal', type: 'picklist' },
  { key: 'signal_name',         label: 'Signal name',         group: 'Signal', type: 'text' },
  { key: 'signal_status',       label: 'Gong flow status',       group: 'Signal', type: 'picklist', values: [...CAMPAIGN_STATUS_ORDER] },
  { key: 'has_responded',       label: 'Responded',       group: 'Signal', type: 'boolean' },
  { key: 'signal_tactic',       label: 'Step type',            group: 'Signal', type: 'picklist' },
  // A9 — `launcher_event_invitees.invitee_type` (`Prospect`, `Customer`, …),
  // Campaign-arm only. Grouped under 'Signal' rather than 'Source': it
  // describes the PERSON on the play, not where the row came from. Sits in the
  // Signal block (not at the end) so the picker prints one "Signal" heading.
  //
  // The key is the bare view column name, NOT `signal_invitee_type`: the
  // bidirectional contract test pins every Signals-catalog key to its
  // `TerraSignalRowSchema`/`TERRA_SIGNALS_FIELD_MAP` twin. The `signal_`
  // prefix exists only on the CONTACT catalog below, where it has to avoid
  // colliding with the contact's own unprefixed columns.
  //
  // Free text, no `values`: the launcher column is uncontrolled (written by a
  // Clay workflow), so any enum here would go stale and — because
  // `validateViewFieldReferences` membership-checks only fields that declare
  // `values` — would start rejecting saved views at write time.
  { key: 'invitee_type',        label: 'Invitee type',        group: 'Signal', type: 'text' },
  // R7 (2026-09-24) — six Signal Action parity columns: the raw
  // (type, subtype, playType) triple the client-side Ranker matches
  // against, plus the flow step/priority/age measures. Real only on the
  // Signal Action arm; typed NULL elsewhere.
  { key: 'signal_source_type',    label: 'Signal type',    group: 'Signal', type: 'picklist' },
  { key: 'signal_source_subtype', label: 'Signal subtype', group: 'Signal', type: 'picklist' },
  { key: 'play_type',             label: 'Play type',        group: 'Signal', type: 'picklist' },
  { key: 'step_number',           label: 'Flow step number',           group: 'Signal', type: 'number' },
  { key: 'priority_stamp',        label: 'Salesforce priority stamp', group: 'Signal', type: 'number' },
  { key: 'days_open',             label: 'Days open',        group: 'Signal', type: 'number' },
  { key: 'signal_instance_id',  label: 'Signal Instance ID',  group: 'Signal', type: 'picklist' },
  { key: 'signal_status_rank',  label: 'Signal Status Rank',  group: 'Signal', type: 'number' },
  { key: 'event_date',          label: 'Signal date',          group: 'Signal', type: 'date' },
  { key: 'due_date',            label: 'Task due date',            group: 'Signal', type: 'date', granularity: 'day' },

  // Action state (the terra_signal_state override is applied inside the view)
  { key: 'action_state',        label: 'Action status',        group: 'Action', type: 'picklist', values: [...ACTION_STATES] },
  { key: 'action_reason',       label: 'Archive reason',       group: 'Action', type: 'text' },
  { key: 'action_completed_at', label: 'Closed date', group: 'Action', type: 'date' },
  // 2026-09-22 "Actioned By" plan: `action_completed_by` (email, unchanged
  // data) relabels to "Actioned By Email"; the two new columns below are the
  // name (the `{me}`-resolvable person field) and its id companion.
  { key: 'action_completed_by',      label: 'Closed by (email)', group: 'Action', type: 'text' },
  { key: 'action_completed_by_id',   label: 'Actioned By ID',    group: 'Action', type: 'text' },
  { key: 'action_completed_by_name', label: 'Closed by',       group: 'Action', type: 'person' },
  { key: 'snooze_until',        label: 'Snoozed until',       group: 'Action', type: 'date' },

  // Who / what the signal is about
  { key: 'account_id',          label: 'Account ID',          group: 'Account', type: 'text' },
  { key: 'account_name',        label: 'Account Name',        group: 'Account', type: 'text' },
  { key: 'contact_id',          label: 'Contact ID',          group: 'Contact', type: 'text' },
  { key: 'contact_name',        label: 'Contact Name',        group: 'Contact', type: 'text' },

  // Assignment
  { key: 'assignee',            label: 'Assignee',            group: 'Assignee', type: 'person' },
  { key: 'assignee_role',       label: 'Assignee role',       group: 'Assignee', type: 'text' },
  { key: 'assignee_manager',    label: 'Assignee manager',    group: 'Assignee', type: 'text' },
  { key: 'assignee_id',         label: 'Assignee ID',         group: 'Assignee', type: 'text' },
  { key: 'assignee_email',      label: 'Assignee email',      group: 'Assignee', type: 'text' },

  // Provenance
  { key: 'source_system',       label: 'Source system',       group: 'Source', type: 'picklist', values: [...SOURCE_SYSTEMS] },
  { key: 'source_record_id',    label: 'Source Record ID',    group: 'Source', type: 'text' },
  { key: 'source_link',         label: 'Link to source',         group: 'Source', type: 'text' },

  // Plays default scope — the one filterOnly entry: EXISTS-backed over
  // `launcher_sfdc_campaigns`, so its alias is out of scope in an outer
  // ORDER BY and the handler must 400 a sort/group_by on it.
  // `allowedOperators: ['equals']` for the same reason as
  // `warm_intro_available` above: the operator evaluates INSIDE the
  // subquery, so `= false` would read as "has a non-visible campaign", not
  // "has no visible campaign".
  {
    key: 'campaign_playhouse_visible',
    label: 'Playhouse Visible',
    group: 'Campaign',
    type: 'boolean',
    filterOnly: true,
    allowedOperators: ['equals'],
  },
];

/**
 * The Signals tab's full filter catalog (Signals PR 1, U2): every
 * `terra_signals` view field above, plus the display-join fields
 * (`SIGNAL_DISPLAY_FILTER_FIELDS`, signal-view.ts — contact/account mirror
 * columns and the Gong Flow join) that exist only at Signals list-route
 * display time. Order is load-bearing for the ordered-concatenation
 * contract test: `TERRA_SIGNALS_FILTER_FIELDS` first, display fields after.
 *
 * U1 (Signals V3 PR 1): the two lists are then folded through
 * `groupContiguous` (each group heading prints once) and `unifyLabels`
 * (shared keys take the column label). The ordered-concat contract is now a
 * set-equality + contiguity contract (flow-signals-list-contract.test.ts).
 *
 * D-M (Signals tab PR 1, review round 1, R19) — one entry is remapped
 * rather than passed through verbatim: `signal_status`'s inherited
 * `values` is `CAMPAIGN_STATUS_ORDER` (the 13 Campaign statuses), but the
 * Signals tab is Signal-Action-only (R2 — every preset scopes to
 * `SIGNAL_SOURCE_CONDITION`), whose real vocabulary is
 * `SIGNAL_ACTION_STATUS_ORDER` (`Running`, `Pending`, `Paused`, `Removed
 * from flow`, `Finished`, `No flow`). Offering Campaign statuses in the
 * Flow status filter let a rep pick a value the arm never produces, and
 * `validateViewFieldReferences`'s R6 membership check then 400'd a saved
 * view filtering on a REAL Signal Action status because it wasn't in the
 * (wrong) enum. The remap is local to this composition — the CONTACT
 * catalog's own `signal_status` entry (filter-fields.ts, Contact section)
 * is untouched and keeps `CAMPAIGN_STATUS_ORDER`.
 */
const SIGNAL_COLUMN_LABELS: ReadonlyMap<string, string> = new Map(SIGNAL_FIELDS.map(f => [f.key, f.label]));

/**
 * Browser-evaluated Signals filter fields (knv.13). Evaluated over the fully
 * loaded list from the row's computed `local_time.status`; no server field-map
 * entry exists on purpose. `SIGNAL_CLIENT_FILTER_KEYS` (below) is the one list
 * of their keys: the list route 400s them, the chip-counts route returns null
 * for a view using one, and the browser strips them from `filters=` and
 * evaluates them over the loaded rows.
 * No per-entry `hint`: a `values` field derives its example line.
 */
const SIGNAL_CLIENT_FILTER_FIELDS: FilterFieldCatalogEntry[] = [
  {
    key: 'business_hours',
    label: 'Business hours',
    group: 'Contact',
    type: 'picklist',
    values: [
      { value: 'open', label: 'Open now (8-6 local)' },
      { value: 'closed', label: 'Closed now' },
      { value: 'unknown', label: 'No location' },
    ],
    allowedOperators: ['equals'],
    andOnly: true,
  },
];

export const SIGNAL_CLIENT_FILTER_KEYS: readonly string[] = SIGNAL_CLIENT_FILTER_FIELDS.map(f => f.key);

export const SIGNAL_FILTER_FIELDS: FilterFieldCatalogEntry[] = withHints(
  groupContiguous(
    unifyLabels(
      [
        ...TERRA_SIGNALS_FILTER_FIELDS.map(f => (f.key === 'signal_status' ? { ...f, values: [...SIGNAL_ACTION_STATUS_ORDER] } : f)),
        ...SIGNAL_DISPLAY_FILTER_FIELDS,
        ...SIGNAL_CLIENT_FILTER_FIELDS,
      ],
      // U1 — Filter, Group and Sort read different lists; a key that is also a
      // Signals column takes the COLUMN label so every picker shows one name.
      SIGNAL_COLUMN_LABELS,
    ),
  ),
  // Picker example lines (field-hints.ts), including the generated Contact / Account fields.
  SIGNAL_CATALOG_HINTS,
);

// ── F5 — write-time field validation for saved views ────────────────────────
// `src/api/flow.ts`'s FIELD_MAP is the query-time allowlist (unknown field →
// 400, `filterOnly` field on sort/group_by → 400); these catalogs are that
// same allowlist's shared source (bidirectional contract tests keep them in
// lockstep). Saved-view CREATE/UPDATE (`src/api/flow-views.ts`) must enforce
// the identical rule at write time — otherwise a saved view can persist a
// sort/group_by/filter field the read path will 500 or 400 on the moment
// someone opens it, and an unknown filter field is silently DROPPED by
// `conditionToSql` at query time, so the view would quietly render more rows
// than its chips claim.

/**
 * LEGACY_VIEW_FIELD_KEYS — grandfathered field keys that were removed from
 * the picker catalogs above (ACCOUNT_FILTER_FIELDS/CONTACT_FILTER_FIELDS,
 * ACCOUNT_FIELDS/CONTACT_FIELDS) but remain valid at query time — the
 * backend FIELD_MAPs in src/api/flow.ts (CONTACT_FIELD_MAP/ACCOUNT_FIELD_MAP)
 * deliberately still support them for existing saved views that persisted
 * them before the removal. F5 write-time validation must keep accepting
 * these on every subsequent write to such a view (changing an unrelated
 * column and pressing Save must not 400 a view just because it predates a
 * picker change) — otherwise the view becomes permanently un-editable
 * without a manual DB fix. This is NOT a hardcoded fallback for missing
 * config: it is a sanctioned exception mirroring FIELD_MAP's own real,
 * intentional read-side support for these keys. New views can never pick
 * these fields (they're absent from the pickers, and this set does not
 * widen the picker surface) — it exists purely to keep old data writable.
 * `tracker_status`'s catalog entry is gone, so there is no `values` list to
 * enforce membership against for it; its historical filter TYPE ('picklist')
 * still comes from here for the structural operator-validity check.
 */
const LEGACY_VIEW_FIELD_KEYS = {
  columns: new Set<string>(['tracker', 'tracker_status']),
  filter: new Map<string, FilterFieldType>([['tracker_status', 'picklist']]),
} as const;

/** Per-entity map of renamed COLUMN keys — today only `'signal'` has any
 *  (`SIGNAL_LEGACY_COLUMN_KEYS`, signal-view.ts). Every other entity is the
 *  identity: nothing to rewrite. */
const LEGACY_COLUMN_KEYS_BY_ENTITY: Readonly<Partial<Record<string, Readonly<Record<string, string>>>>> = {
  signal: SIGNAL_LEGACY_COLUMN_KEYS,
};

/** Per-entity COLUMN keys that no longer exist and are removed (not renamed)
 *  from a saved definition's `columns` — Signals' `priority` number, dropped in
 *  V3 PR 1 U5. `sort`/`groupBy` are never touched: `priority` is still a valid
 *  SORT key (the ranking), and a drop there would change a view's order. */
const DROPPED_COLUMN_KEYS_BY_ENTITY: Readonly<Partial<Record<string, ReadonlySet<string>>>> = {
  signal: new Set(SIGNAL_DROPPED_COLUMN_KEYS),
};

/** The three definition slots `migrateLegacyViewFieldKeys` can rewrite a
 *  renamed COLUMN key inside. Deliberately excludes `filter` (fix-plan-batch3.md
 *  §1.2 — a filter condition is picked from the FILTER catalog, which never
 *  renamed the key, so it must never be rewritten here). */
export interface LegacyMigratableDefinition {
  sort: { field: string };
  groupBy: string[];
  columns: string[];
}

/**
 * Rewrites renamed COLUMN keys (`LEGACY_COLUMN_KEYS_BY_ENTITY`) in `columns`,
 * and removes dropped ones (`DROPPED_COLUMN_KEYS_BY_ENTITY`) from `columns` only,
 * `groupBy` and `sort.field` for `entity` — today only `'signal'` has any
 * renames (QA batch 3 fix-plan D-B: the Subtype column's key moved from
 * `signal_subtype` to `signal_source_subtype`, but `signal_subtype` is still
 * a real, unrelated FILTER field, so `filter` is never touched). `columns` is
 * de-duplicated on rewrite, keeping the FIRST occurrence's position — a
 * legacy `columns` array can otherwise carry both the old and new key
 * (a rep who saved before AND after the rename in the same working state).
 *
 * Returns the SAME reference when nothing changed, so a caller's equality
 * check (a dirty diff, a `toBe` in a test) is unaffected by running this
 * unconditionally on every read and write.
 */
export function migrateLegacyViewFieldKeys<D extends LegacyMigratableDefinition>(entity: string, def: D): D {
  const renames = LEGACY_COLUMN_KEYS_BY_ENTITY[entity];
  const dropped = DROPPED_COLUMN_KEYS_BY_ENTITY[entity];
  if (!renames && !dropped) return def;

  const renameKey = (key: string): string => renames?.[key] ?? key;

  const nextSortField = renameKey(def.sort.field);
  const nextGroupBy = def.groupBy.map(renameKey);
  const seen = new Set<string>();
  const nextColumns: string[] = [];
  let columnsChanged = false;
  for (const key of def.columns) {
    if (dropped?.has(key)) {
      columnsChanged = true;
      continue;
    }
    const renamed = renameKey(key);
    if (renamed !== key) columnsChanged = true;
    if (seen.has(renamed)) continue;
    seen.add(renamed);
    nextColumns.push(renamed);
  }

  const sortChanged = nextSortField !== def.sort.field;
  const groupByChanged = nextGroupBy.some((key, i) => key !== def.groupBy[i]);
  if (!sortChanged && !groupByChanged && !columnsChanged) return def;

  return {
    ...def,
    sort: sortChanged ? { ...def.sort, field: nextSortField } : def.sort,
    groupBy: groupByChanged ? nextGroupBy : def.groupBy,
    columns: columnsChanged ? nextColumns : def.columns,
  };
}

/** The saved-view entities. `'play'` (plan 2026-11-03-001 U1) is the Plays
 *  tab: its catalog is `PLAYS_FILTER_FIELDS`, its columns `PLAYS_TABLE_COLUMNS`,
 *  and its sort keys are the client-side `PLAYS_SORTABLE_KEYS`. */
export type FlowViewEntity = 'contact' | 'account' | 'signal' | 'play';

function filterCatalogByKey(entity: FlowViewEntity): Map<string, FilterFieldCatalogEntry> {
  const catalog = entity === 'account' ? ACCOUNT_FILTER_FIELDS : entity === 'signal' ? SIGNAL_FILTER_FIELDS : entity === 'play' ? PLAYS_FILTER_FIELDS : CONTACT_FILTER_FIELDS;
  return new Map(catalog.map(f => [f.key, f]));
}

/**
 * Whether `fieldKey` is a valid FILTER-field reference for `entity` — the
 * live picker catalog OR the grandfathered legacy set (`LEGACY_VIEW_FIELD_KEYS`
 * above). One membership check, reused by `validateViewFieldReferences`'s
 * write-time 400 and by `useViewStateMachine`'s view-switch filter carry
 * (docs/flow/custom-views-notes.md) to prune a carried condition whose field
 * the TARGET view's entity doesn't recognize — without duplicating the
 * catalog/legacy-set union that function already owns.
 */
export function isKnownFilterField(entity: FlowViewEntity, fieldKey: string): boolean {
  return filterCatalogByKey(entity).has(fieldKey) || LEGACY_VIEW_FIELD_KEYS.filter.has(fieldKey);
}

function visibleColumnKeys(entity: FlowViewEntity): Set<string> {
  const fields = entity === 'account' ? ACCOUNT_FIELDS : entity === 'signal' ? SIGNAL_FIELDS : entity === 'play' ? PLAYS_TABLE_COLUMNS : CONTACT_FIELDS;
  return new Set(fields.map(f => f.key));
}

/** Sort/column keys that resolve to neither the filter catalog NOR a real
 *  view/join column — client-computed values attached to each row after
 *  fetch (`SIGNAL_SYNTHETIC_SORT_KEYS`, e.g. the Ranker's `priority`).
 *  Only `'signal'` has any today; every other entity's set is empty. */
function syntheticSortKeysFor(entity: FlowViewEntity): ReadonlySet<string> {
  if (entity === 'play') return PLAYS_SYNTHETIC_SORT_KEYS;
  return entity === 'signal' ? new Set(SIGNAL_SYNTHETIC_SORT_KEYS) : EMPTY_SYNTHETIC_KEYS;
}
const EMPTY_SYNTHETIC_KEYS: ReadonlySet<string> = new Set();
/** `'play'` sorts client-side on `PLAYS_SORTABLE_KEYS` (no server column). */
const PLAYS_SYNTHETIC_SORT_KEYS: ReadonlySet<string> = new Set(PLAYS_SORTABLE_KEYS);

/** Sibling of `syntheticSortKeysFor` for `groupBy` (QA batch 3, D-A step 2):
 *  `local_time`/`business_hours` are legitimate GROUP levels for `'signal'`,
 *  arranged entirely client-side (`arrangeSignalsClientSide`, G3) — neither
 *  resolves to the filter catalog NOR a real view/join column. */
function syntheticGroupKeysFor(entity: FlowViewEntity): ReadonlySet<string> {
  return entity === 'signal' ? new Set(SIGNAL_SYNTHETIC_GROUP_KEYS) : EMPTY_SYNTHETIC_KEYS;
}

/** The subset of a view definition F5 cares about — deliberately loose
 *  (`groupBy` accepts every shape `normalizeGroupBy` does) so it accepts both
 *  a freshly normalized definition and a raw create/update body. */
export interface ViewFieldReferences {
  filter: FilterGroup[];
  groupLogic?: GroupLogic;
  sort: { field: string };
  groupBy?: string[] | string | null;
  columns: string[];
}

/**
 * A condition on an `andOnly` field (browser-evaluated, e.g. `business_hours`) is allowed only when its group
 * is `AND` or holds a single condition, AND the outer `groupLogic` is `AND` or at most one group is non-empty.
 * Anything else cannot be computed from the server result, so it is rejected instead of silently read as AND.
 * Returns `"<Label> can only be combined with And"` for the first violating condition, else `null`.
 */
export function andOnlyPlacementError(
  filter: FilterGroup[],
  groupLogic: GroupLogic,
  catalog: ReadonlyMap<string, FilterFieldCatalogEntry> | readonly FilterFieldCatalogEntry[],
): string | null {
  const byKey = new Map<string, FilterFieldCatalogEntry>(
    catalog instanceof Map ? catalog : (catalog as readonly FilterFieldCatalogEntry[]).map(f => [f.key, f] as const),
  );
  const nonEmptyGroups = filter.filter(g => g.conditions.length > 0).length;
  const outerOrMixes = groupLogic === 'OR' && nonEmptyGroups > 1;
  for (const group of filter) {
    const groupOrMixes = group.logic === 'OR' && group.conditions.length > 1;
    for (const condition of group.conditions) {
      const entry = byKey.get(condition.field);
      if (entry?.andOnly && (groupOrMixes || outerOrMixes)) return `${entry.label} can only be combined with And`;
    }
  }
  return null;
}

/**
 * Validates every field reference in a saved-view definition against the
 * entity's catalogs. Returns one message per violation — naming the field
 * and which slot it was in — or an empty array when the definition is clean.
 * Never partial: every slot is checked so a single 400 names every problem
 * at once rather than round-tripping one violation per request.
 */
export function validateViewFieldReferences(entity: FlowViewEntity, def: ViewFieldReferences): string[] {
  const errors: string[] = [];
  const filterCatalog = filterCatalogByKey(entity);
  const columns = visibleColumnKeys(entity);

  const placementError = andOnlyPlacementError(def.filter, def.groupLogic ?? 'AND', filterCatalog);
  if (placementError) errors.push(placementError);

  for (const group of def.filter) {
    for (const condition of group.conditions) {
      const fieldDef = filterCatalog.get(condition.field);
      // Grandfathered exception (see LEGACY_VIEW_FIELD_KEYS doc comment): a
      // key like `tracker_status` has no catalog entry any more, but a
      // pre-existing saved view may still carry it — fall back to its
      // historical type rather than reporting "unknown field".
      const legacyType = LEGACY_VIEW_FIELD_KEYS.filter.get(condition.field);
      if (!fieldDef && legacyType === undefined) {
        errors.push(`filter: unknown field "${condition.field}"`);
        continue;
      }
      const type = fieldDef ? fieldDef.type : legacyType!;
      // R1 — an operator invalid for this field's type (or invalid for ANY
      // type, e.g. a typo'd operator string) must 400 here rather than
      // reach conditionToSql, which silently DROPS an unrecognized operator
      // from the WHERE clause — the view would 201 and look saved, but
      // render as if that condition were never added (a hidden filter).
      // This check still runs for a legacy key, using its historical type.
      if (!isValidOperatorForType(type, condition.operator)) {
        errors.push(
          `filter: operator "${condition.operator}" is not valid for field "${condition.field}" (type "${type}")`,
        );
        continue;
      }
      // U6 — an hour window needs a clock; a calendar-DATE field has none.
      if (fieldDef?.granularity === 'day' && RELATIVE_HOUR_OPERATORS.has(condition.operator)) {
        errors.push(
          `filter: operator "${condition.operator}" is not valid for field "${condition.field}" — it is a calendar date, so use whole days`,
        );
        continue;
      }
      // Field-level operator narrowing (`allowedOperators`, see the catalog
      // entry's doc comment) — a stricter allowlist than the type-level check
      // above. A legacy key has no catalog entry, so there is nothing to
      // narrow against.
      if (fieldDef?.allowedOperators && !fieldDef.allowedOperators.includes(condition.operator)) {
        errors.push(
          `filter: operator "${condition.operator}" is not allowed for field "${condition.field}" (allowed: ${fieldDef.allowedOperators.join(', ')})`,
        );
        continue;
      }
      // A legacy key has no catalog entry, so there is no `values` list to
      // enforce membership against — passing `undefined` skips that check.
      // `valueStringsOf` (A1): a labeled-option field validates against its
      // STORED values (record ids), never the display labels.
      const valueError = validateOperatorValueShape(
        type,
        condition.operator,
        condition.value,
        fieldDef ? valueStringsOf(fieldDef) : undefined,
      );
      if (valueError) {
        errors.push(`filter: field "${condition.field}" — ${valueError}`);
      }
    }
  }

  const sortDef = filterCatalog.get(def.sort.field);
  // A synthetic key (client-computed, e.g. Signals' `priority` Ranker rank)
  // has no catalog entry by definition — it is a legitimate sort target
  // for its entity without being a filter/column-catalog member.
  if (entity === 'play' && !PLAYS_SYNTHETIC_SORT_KEYS.has(def.sort.field)) {
    // The Plays catalog also lists display-only fields (city, status, …) that
    // the client-side sort has no comparator for — only PLAYS_SORTABLE_KEYS sort.
    errors.push(`sort: field "${def.sort.field}" is not sortable on plays`);
  } else if (!sortDef && !syntheticSortKeysFor(entity).has(def.sort.field)) {
    errors.push(`sort: unknown field "${def.sort.field}"`);
  } else if (sortDef?.filterOnly) {
    errors.push(`sort: field "${def.sort.field}" is filter-only and cannot be sorted on`);
  }

  // U4 — an ordered, up-to-MAX_GROUP_LEVELS list of levels; every level is
  // checked independently (an early valid level must not mask a later
  // invalid one), and the error names the offending level by INDEX so a
  // 4-level or a mixed valid/invalid groupBy gets one message per problem,
  // same "never partial" contract every other slot here already has.
  normalizeGroupBy(def.groupBy).forEach((key, idx) => {
    const groupDef = filterCatalog.get(key);
    // A synthetic group key (client-computed, e.g. Signals' `local_time`/
    // `business_hours`) has no catalog entry by definition — it is a
    // legitimate groupBy level for its entity without being a filter/column-
    // catalog member (mirrors the `syntheticSortKeysFor` sort rule above).
    if (entity === 'play' && !(PLAYS_GROUP_KEYS as readonly string[]).includes(key)) {
      errors.push(`groupBy[${idx}]: field "${key}" cannot be grouped on in plays`);
    } else if (!groupDef && !syntheticGroupKeysFor(entity).has(key)) {
      errors.push(`groupBy[${idx}]: unknown field "${key}"`);
    } else if (groupDef?.filterOnly) {
      errors.push(`groupBy[${idx}]: field "${key}" is filter-only and cannot be grouped on`);
    }
  });

  for (const column of def.columns) {
    // Grandfathered exception (see LEGACY_VIEW_FIELD_KEYS doc comment):
    // `tracker`/`tracker_status` are removed from the column catalogs but
    // remain legitimate, renderable columns for a pre-existing saved view.
    if (!columns.has(column) && !LEGACY_VIEW_FIELD_KEYS.columns.has(column)) {
      errors.push(`columns: unknown field "${column}"`);
    }
  }

  return errors;
}
